import React, { Dispatch, SetStateAction } from 'react';

import {
  AttributeGroupUpdateBulkInputItem,
  AttributeUpdateBulkTempItem,
  ConversionUnit,
  SchemaTree,
  SchemaTreeAttribute,
  SchemaTreeAttributeGroup,
  SelectedAttributeRoute,
} from '@atom/types/schema';

export enum SchemaDetailView {
  PREVIEW = 'PREVIEW',
  BUILDER = 'BUILDER',
}

export enum PreviewTab {
  INFO = 'INFO',
  SUB_ITEMS = 'SUB_ITEMS',
  COST = 'COST',
}

export enum DragDropType {
  ATTRIBUTE = 'attribute',
  ATTRIBUTE_GROUP = 'attributeGroup',
  ENUM_OPTION = 'enumOption',
}

// Dictionary of schemaId (element) to dictionary of attributeGroupId and pending changes
export interface PendingGroupUpdates {
  [schemaId: string]: {
    [attributeGroupId: string]: AttributeGroupUpdateBulkInputItem;
  };
}

// Dictionary of schemaId (element) to dictionary of attributeId and pending changes
export interface PendingUpdates {
  [schemaId: string]: {
    [attributeId: string]: AttributeUpdateBulkTempItem;
  };
}

// Dictionary of schemaId (element) to dictionary of attributeGroupId to dictionary of temp attributes with attributeId
export interface PendingCreations {
  [schemaId: string]: {
    [attributeGroupId: string]: {
      [attributeId: string]: SchemaTreeAttribute;
    };
  };
}

// Dictionary of schemaId (element) with pending changes to reorder groups/attributes
export interface PendingSchemaUpdates {
  [schemaId: string]: SchemaTreeAttributeGroup[];
}

// Working copy of the root schema's full conversion unit list on a published
// schema, including new rows with client ids. null when nothing has changed.
export type PendingConversionUnits = ConversionUnit[] | null;

interface Context {
  schemaTree: Partial<SchemaTree>;
  refetchSchemaTree: () => Promise<any>;
  loading: boolean;
  view: SchemaDetailView;
  setView: Dispatch<SetStateAction<SchemaDetailView>>;
  previewTab: PreviewTab;
  setPreviewTab: Dispatch<SetStateAction<PreviewTab>>;
  collapsedSubItems: Set<string>;
  setCollapsedSubItems: Dispatch<SetStateAction<Set<string>>>;
  selectedSubItem: Partial<SchemaTree>;
  setSelectedSubItem: Dispatch<SetStateAction<Partial<SchemaTree>>>;
  selectedSubItemPath: any[];
  setSelectedSubItemPath: Dispatch<SetStateAction<any[]>>;
  selectedAttributeRoute: SelectedAttributeRoute;
  setSelectedAttributeRoute: Dispatch<SetStateAction<SelectedAttributeRoute>>;
  selectedAttribute: Partial<SchemaTreeAttribute>;
  setSelectedAttribute: Dispatch<SetStateAction<Partial<SchemaTreeAttribute>>>;
  selectedAttributeGroup: Partial<SchemaTreeAttributeGroup>;
  setSelectedAttributeGroup: Dispatch<
    SetStateAction<Partial<SchemaTreeAttributeGroup>>
  >;
  pendingGroupUpdates: PendingGroupUpdates;
  setPendingGroupUpdates: Dispatch<SetStateAction<PendingGroupUpdates>>;
  pendingUpdates: PendingUpdates;
  setPendingUpdates: Dispatch<SetStateAction<PendingUpdates>>;
  pendingCreations: PendingCreations;
  setPendingCreations: Dispatch<SetStateAction<PendingCreations>>;
  pendingSchemaUpdates: PendingSchemaUpdates;
  setPendingSchemaUpdates: Dispatch<SetStateAction<PendingSchemaUpdates>>;
  pendingConversionUnits: PendingConversionUnits;
  setPendingConversionUnits: Dispatch<SetStateAction<PendingConversionUnits>>;
}

export const initialState: Context = {
  schemaTree: {},
  refetchSchemaTree: () => Promise.resolve({}),
  loading: false,
  view: SchemaDetailView.PREVIEW,
  setView: () => {},
  previewTab: PreviewTab.INFO,
  setPreviewTab: () => {},
  collapsedSubItems: new Set([]),
  setCollapsedSubItems: () => {},
  selectedSubItem: {},
  setSelectedSubItem: () => {},
  selectedSubItemPath: null,
  setSelectedSubItemPath: () => {},
  selectedAttributeRoute: null,
  setSelectedAttributeRoute: () => {},
  selectedAttribute: {},
  setSelectedAttribute: () => {},
  selectedAttributeGroup: {},
  setSelectedAttributeGroup: () => {},
  pendingGroupUpdates: {},
  setPendingGroupUpdates: () => {},
  pendingUpdates: {},
  setPendingUpdates: () => {},
  pendingCreations: {},
  setPendingCreations: () => {},
  pendingSchemaUpdates: {},
  setPendingSchemaUpdates: () => {},
  pendingConversionUnits: null,
  setPendingConversionUnits: () => {},
};

const SchemaDetailContext = React.createContext<Context>(initialState);

export default SchemaDetailContext;
